import React, { useEffect, useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Icons from "react-feather";
import Modal from "components/Modal";
import { CheckBox, Loading, MultiSelect } from "components/form";
import { axiosInstance, handleErrors, handleSuccessMessages } from "store/auth";
import { useCore } from "store/core";

const PRODUCT_TYPES = ["Movie", "Serie", "Music", "Screening", "SerieEpisode"];

const normalizeOptions = (list = []) => {
  return (list || [])
    .map((item) => {
      if (
        item &&
        typeof item === "object" &&
        "value" in item &&
        "label" in item
      ) {
        return item;
      }

      if (item && typeof item === "object" && "id" in item) {
        return {
          value: item.id,
          label:
            item.title ||
            item.name ||
            item.fa_name ||
            item.display_name ||
            item.user_name ||
            `#${item.id}`,
        };
      }

      return null;
    })
    .filter(Boolean);
};

const toIdList = (list = []) =>
  (list || [])
    .map((item) => item?.id)
    .filter((id) => Number.isInteger(id) || /^\d+$/.test(String(id)))
    .map((id) => Number(id));

const buildEffectiveUrl = (userId, page, filters) => {
  let url = `/panel/catalog-users/${userId}/effective-products?page=${page}&perpage=10`;

  if (filters.search) {
    url += `&search=${encodeURIComponent(filters.search)}`;
  }

  if (filters.type) {
    url += `&type=${encodeURIComponent(filters.type)}`;
  }

  if (filters.category_id) {
    url += `&category_id=${filters.category_id}`;
  }

  return url;
};

export default function ManageAccessModal({ show, user, onClose, onSaved }) {
  const core = useCore();
  const [loading, setLoading] = useState(false);
  const [accessData, setAccessData] = useState(null);

  const [effectiveLoading, setEffectiveLoading] = useState(false);
  const [effectiveProducts, setEffectiveProducts] = useState([]);
  const [effectiveMeta, setEffectiveMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  const [effectiveFilters, setEffectiveFilters] = useState({
    search: "",
    type: "",
    category_id: "",
  });

  const loadEffectiveProducts = async (page = 1, customFilters = null) => {
    if (!user?.id) {
      return;
    }

    const filters = customFilters || effectiveFilters;

    setEffectiveLoading(true);
    try {
      const result = await axiosInstance.get(
        buildEffectiveUrl(user.id, page, filters)
      );
      const payload = result.data || {};

      setEffectiveProducts(payload.data || []);
      setEffectiveMeta(
        payload.meta || { current_page: 1, last_page: 1, total: 0 }
      );
    } catch (e) {
      handleErrors(e);
    } finally {
      setEffectiveLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!show || !user?.id) {
        return;
      }

      setLoading(true);
      try {
        await Promise.all([
          core.dispatch(core.getProducts()),
          core.dispatch(core.getProductCategories()),
        ]);

        const accessResult = await axiosInstance.get(
          `/panel/catalog-users/${user.id}/access`
        );
        setAccessData(accessResult.data);

        await loadEffectiveProducts(1, {
          search: "",
          type: "",
          category_id: "",
        });
      } catch (e) {
        handleErrors(e);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
    // Core actions are store-provided and this request lifecycle is keyed by modal/user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, user?.id]);

  const handleRemoveProduct = async (productId) => {
    try {
      await axiosInstance.put(`/panel/catalog-users/${user.id}/access`, {
        mode: "remove",
        product_ids: [productId],
        category_ids: [],
      });

      handleSuccessMessages("محصول از دسترسی کاربر حذف شد");

      const updatedAccess = await axiosInstance.get(
        `/panel/catalog-users/${user.id}/access`
      );
      setAccessData(updatedAccess.data);

      await loadEffectiveProducts(effectiveMeta.current_page || 1);
    } catch (e) {
      handleErrors(e);
    }
  };

  const productOptions = useMemo(() => {
    const direct = normalizeOptions(
      (accessData?.direct_products || []).map((item) => ({
        value: item.id,
        label: `${item.title || "-"} (${item.type || "-"}) - #${item.id}`,
      }))
    );

    const all = normalizeOptions(core.products || []);
    const merged = [...direct, ...all];
    const byId = new Map();

    merged.forEach((option) => {
      if (!byId.has(option.value)) {
        byId.set(option.value, option);
      }
    });

    return Array.from(byId.values());
  }, [core.products, accessData]);

  const categoryOptions = useMemo(() => {
    const direct = normalizeOptions(
      (accessData?.categories || []).map((item) => ({
        value: item.id,
        label: `${item.name || "-"} (${item.type || "-"}) - #${item.id}`,
      }))
    );

    const all = normalizeOptions(core.productCategories || []);
    const merged = [...direct, ...all];
    const byId = new Map();

    merged.forEach((option) => {
      if (!byId.has(option.value)) {
        byId.set(option.value, option);
      }
    });

    return Array.from(byId.values());
  }, [core.productCategories, accessData]);

  const initialValues = useMemo(
    () => ({
      mode: "sync",
      product_ids: toIdList(accessData?.direct_products),
      category_ids: toIdList(accessData?.categories),
      all_plus_products: !!accessData?.all_plus_products,
    }),
    [accessData]
  );

  const directProductIds = useMemo(
    () => new Set(toIdList(accessData?.direct_products)),
    [accessData]
  );

  const resetAndClose = () => {
    setAccessData(null);
    setEffectiveProducts([]);
    setEffectiveMeta({ current_page: 1, last_page: 1, total: 0 });
    setEffectiveFilters({ search: "", type: "", category_id: "" });
    onClose();
  };

  return (
    <Modal
      show={show}
      handleClose={resetAndClose}
      title={`مدیریت دسترسی کاتالوگ${
        user?.full_name ? ` - ${user.full_name}` : ""
      }`}
      size="modal-xl catalog-access-modal"
    >
      {loading && (
        <div className="modal-body flex min-h-[240px] items-center justify-center">
          <Loading />
        </div>
      )}

      {!loading && accessData && (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const payload = {
                mode: values.mode,
                product_ids: values.product_ids || [],
                category_ids: values.category_ids || [],
                all_plus_products: !!values.all_plus_products,
              };

              const result = await axiosInstance.put(
                `/panel/catalog-users/${user.id}/access`,
                payload
              );

              setAccessData(result.data);
              await loadEffectiveProducts(1);

              handleSuccessMessages("دسترسی کاربر با موفقیت ذخیره شد");
              if (onSaved) {
                onSaved();
              }
            } catch (e) {
              handleErrors(e);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, values }) => (
            <Form>
              <div className="modal-body space-y-5 bg-gray-50 dark:bg-dark-2">
                <section className="grid gap-3 lg:grid-cols-3">
                  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-5 dark:bg-dark-3">
                    <label
                      className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-300"
                      htmlFor="mode"
                    >
                      حالت اعمال دسترسی
                    </label>
                    <Field
                      as="select"
                      id="mode"
                      name="mode"
                      className="catalog-access-control form-control w-full text-sm"
                    >
                      <option value="sync">جایگزینی کامل (sync)</option>
                      <option value="add">افزودن به دسترسی‌ها (add)</option>
                      <option value="remove">حذف از دسترسی‌ها (remove)</option>
                    </Field>
                  </div>

                  <div className="flex items-center rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-5 dark:bg-dark-3">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900 dark:bg-opacity-30 dark:text-blue-300">
                      <Icons.Package className="h-5 w-5" />
                    </span>
                    <div className="mr-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        محصولات مستقیم
                      </div>
                      <div className="mt-1 text-lg font-semibold text-gray-800 dark:text-gray-100">
                        {accessData?.direct_products?.length || 0}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-5 dark:bg-dark-3">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600 dark:bg-green-900 dark:bg-opacity-30 dark:text-green-300">
                      <Icons.Layers className="h-5 w-5" />
                    </span>
                    <div className="mr-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        محصولات مؤثر
                      </div>
                      <div className="mt-1 text-lg font-semibold text-gray-800 dark:text-gray-100">
                        {accessData?.effective_products_count || 0}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-5 dark:bg-dark-3">
                  <div className="mb-4 flex items-start">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 text-theme-1 dark:bg-dark-2 dark:text-blue-300">
                      <Icons.Shield className="h-4 w-4" />
                    </span>
                    <div className="mr-3">
                      <h3 className="font-medium text-gray-800 dark:text-gray-100">
                        تخصیص دسترسی
                      </h3>
                      <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                        محصول‌ها و دسته‌بندی‌های قابل دسترس این کاربر را انتخاب
                        کنید.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field
                      label="محصولات مستقیم"
                      name="product_ids"
                      valueOnly
                      component={MultiSelect}
                      options={productOptions}
                      appearance="catalog"
                      wrapperClassName="col-span-1 min-w-0"
                    />

                    <Field
                      label="دسته‌بندی‌ها"
                      name="category_ids"
                      valueOnly
                      component={MultiSelect}
                      options={categoryOptions}
                      appearance="catalog"
                      wrapperClassName="col-span-1 min-w-0"
                    />
                  </div>

                  <div className="mt-4 flex items-start rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-yellow-700 dark:border-opacity-40 dark:bg-yellow-900 dark:bg-opacity-10">
                    <Icons.Star className="ml-3 mt-1 h-5 w-5 flex-shrink-0 text-amber-500" />
                    <div>
                      <Field
                        label="دسترسی به همه محصولات پلاس"
                        name="all_plus_products"
                        component={CheckBox}
                        wrapperClassName="catalog-plus-checkbox"
                        labelClassName="inline-flex cursor-pointer items-center"
                        inputClassName="form-checkbox ml-2 h-5 w-5 rounded border-gray-400 text-theme-1"
                        textClassName="text-sm font-medium text-gray-800 dark:text-gray-100"
                      />
                      <p className="mt-2 text-xs leading-6 text-gray-600 dark:text-gray-400">
                        با فعال‌کردن این گزینه، محصولات پلاس فعلی و محصولاتی که
                        در آینده پلاس می‌شوند، به‌صورت خودکار در دسترس کاربر
                        قرار می‌گیرند.
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex items-start rounded-lg bg-blue-50 p-3 text-xs leading-6 text-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-200">
                    <Icons.Info className="ml-2 mt-1 h-4 w-4 flex-shrink-0" />
                    <p>
                      در حالت <b>افزودن</b> موارد جدید اضافه، در حالت <b>حذف</b>{" "}
                      موارد انتخاب‌شده حذف و در حالت <b>جایگزینی کامل</b> فقط
                      انتخاب‌های فعلی نگه داشته می‌شوند.
                    </p>
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 text-xs dark:border-dark-5">
                    <span className="text-gray-500 dark:text-gray-400">
                      تعداد انتخاب‌شده
                    </span>
                    <div className="flex gap-2">
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700 dark:bg-dark-2 dark:text-gray-300">
                        {values.product_ids?.length || 0} محصول
                      </span>
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700 dark:bg-dark-2 dark:text-gray-300">
                        {values.category_ids?.length || 0} دسته‌بندی
                      </span>
                    </div>
                  </div>
                </section>

                <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-5 dark:bg-dark-3">
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-800 dark:text-gray-100">
                      محصولات مؤثر کاربر
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                      نتیجهٔ نهایی دسترسی‌های مستقیم، دسته‌بندی‌ها و محصولات
                      پلاس
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="text-xs">
                      <label className="mb-2 block font-medium text-gray-600 dark:text-gray-300">
                        جستجو
                      </label>
                      <input
                        aria-label="جستجوی محصولات موثر"
                        className="catalog-access-control form-control w-full text-sm"
                        value={effectiveFilters.search}
                        onChange={({ target: { value } }) =>
                          setEffectiveFilters((prev) => ({
                            ...prev,
                            search: value,
                          }))
                        }
                        placeholder="عنوان محصول"
                      />
                    </div>

                    <div className="text-xs">
                      <label className="mb-2 block font-medium text-gray-600 dark:text-gray-300">
                        نوع محصول
                      </label>
                      <select
                        aria-label="نوع محصول موثر"
                        className="catalog-access-control form-control w-full text-sm"
                        value={effectiveFilters.type}
                        onChange={({ target: { value } }) =>
                          setEffectiveFilters((prev) => ({
                            ...prev,
                            type: value,
                          }))
                        }
                      >
                        <option value="">همه انواع</option>
                        {PRODUCT_TYPES.map((item) => (
                          <option value={item} key={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="text-xs">
                      <label className="mb-2 block font-medium text-gray-600 dark:text-gray-300">
                        دسته‌بندی
                      </label>
                      <select
                        aria-label="دسته‌بندی محصول موثر"
                        className="catalog-access-control form-control w-full text-sm"
                        value={effectiveFilters.category_id}
                        onChange={({ target: { value } }) =>
                          setEffectiveFilters((prev) => ({
                            ...prev,
                            category_id: value,
                          }))
                        }
                      >
                        <option value="">همه دسته‌بندی‌ها</option>
                        {categoryOptions.map((item) => (
                          <option
                            value={item.value}
                            key={`cat-filter-${item.value}`}
                          >
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end gap-2">
                      <button
                        type="button"
                        className="btn btn-primary flex-1"
                        onClick={() => loadEffectiveProducts(1)}
                        disabled={effectiveLoading}
                      >
                        <Icons.Filter className="ml-2 h-4 w-4" />
                        اعمال
                      </button>
                      <button
                        type="button"
                        aria-label="پاکسازی فیلترها"
                        title="پاکسازی فیلترها"
                        className="btn btn-outline-secondary px-3"
                        onClick={() => {
                          const resetFilters = {
                            search: "",
                            type: "",
                            category_id: "",
                          };
                          setEffectiveFilters(resetFilters);
                          loadEffectiveProducts(1, resetFilters);
                        }}
                        disabled={effectiveLoading}
                      >
                        <Icons.RotateCcw className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-5">
                    <table className="table table-report catalog-access-table min-w-[760px]">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-dark-2">
                          <th className="whitespace-nowrap text-center text-xs">
                            شناسه
                          </th>
                          <th className="whitespace-nowrap text-right text-xs">
                            عنوان
                          </th>
                          <th className="whitespace-nowrap text-center text-xs">
                            نوع
                          </th>
                          <th className="whitespace-nowrap text-center text-xs">
                            قیمت
                          </th>
                          <th className="whitespace-nowrap text-center text-xs">
                            تاریخ ایجاد
                          </th>
                          <th className="whitespace-nowrap text-center text-xs">
                            نوع دسترسی / عملیات
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {effectiveLoading && (
                          <tr>
                            <td colSpan={6} className="text-center p-4">
                              <Loading />
                            </td>
                          </tr>
                        )}

                        {!effectiveLoading && effectiveProducts.length < 1 && (
                          <tr>
                            <td colSpan={6} className="text-center p-4">
                              محصولی یافت نشد.
                            </td>
                          </tr>
                        )}

                        {!effectiveLoading &&
                          effectiveProducts.map((item) => (
                            <tr
                              className="intro-x"
                              key={`effective-product-${item.id}`}
                            >
                              <td className="text-center">{item.id}</td>
                              <td className="text-right font-medium">
                                {item.title || "-"}
                              </td>
                              <td className="text-center">
                                {item.type || "-"}
                              </td>
                              <td className="text-center">
                                {item?.price?.amount || 0}
                              </td>
                              <td
                                className="whitespace-nowrap text-center"
                                dir="ltr"
                              >
                                {item.created_at || "-"}
                              </td>
                              <td className="text-center">
                                {directProductIds.has(Number(item.id)) ? (
                                  <button
                                    type="button"
                                    aria-label={`remove-direct-product-${item.id}`}
                                    className="inline-flex items-center whitespace-nowrap rounded-md px-2 py-1 text-xs text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900 dark:hover:bg-opacity-20"
                                    onClick={() => handleRemoveProduct(item.id)}
                                  >
                                    <Icons.Trash2 className="ml-1 h-4 w-4" />
                                    حذف دسترسی مستقیم
                                  </button>
                                ) : (
                                  <span className="inline-block whitespace-nowrap rounded-full bg-green-50 px-2 py-1 text-xs text-green-700 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-300">
                                    از طریق دسته‌بندی یا پلاس
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      تعداد کل:{" "}
                      <b className="text-gray-800 dark:text-gray-200">
                        {effectiveMeta.total || 0}
                      </b>{" "}
                      محصول
                    </div>
                    <div className="flex gap-2 self-end sm:self-auto">
                      <button
                        type="button"
                        className="btn btn-outline-secondary flex w-24 items-center justify-center"
                        disabled={
                          effectiveLoading ||
                          (effectiveMeta.current_page || 1) <= 1
                        }
                        onClick={() =>
                          loadEffectiveProducts(
                            (effectiveMeta.current_page || 1) - 1
                          )
                        }
                      >
                        <Icons.ChevronRight className="ml-1 h-4 w-4" />
                        قبلی
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary flex w-24 items-center justify-center"
                        disabled={
                          effectiveLoading ||
                          (effectiveMeta.current_page || 1) >=
                            (effectiveMeta.last_page || 1)
                        }
                        onClick={() =>
                          loadEffectiveProducts(
                            (effectiveMeta.current_page || 1) + 1
                          )
                        }
                      >
                        بعدی
                        <Icons.ChevronLeft className="mr-1 h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </section>
              </div>

              <div className="modal-footer bg-white dark:bg-dark-3">
                <button
                  className="btn btn-primary inline-flex w-28 items-center justify-center"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loading />
                  ) : (
                    <>
                      <Icons.Save className="ml-2 h-4 w-4" />
                      ذخیره
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={resetAndClose}
                  className="btn btn-outline-secondary mr-2 inline-flex w-24 items-center justify-center"
                >
                  بستن
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
}
